/**
 * The web's native bridge: there is no phone to talk to, so every method
 * resolves and does nothing.
 *
 * That is the correct behaviour, not a stub awaiting work. On the web the
 * session lives in localStorage, the ledger in IndexedDB, and there is no
 * widget, outbox, haptic engine or notification scheduler to keep in step —
 * so `hasOutbox` is false and nothing downstream waits on a mirror that does
 * not exist.
 */

import { webPurchases } from "./purchases";
import type { NativeBridge } from "./bridge";

const done = (): Promise<void> => Promise.resolve();

export const webBridge: NativeBridge = {
  platform: "web",
  setTheme: done,
  purchases: webPurchases,
  hasOutbox: false,
  // The web signs in through Google's own button (GIS), not an OS sheet.
  signInProviders: [],
  signIn: () => Promise.reject(new Error("There is no OS sign-in sheet on the web.")),

  markSignedOut: done,
  signOutPending: () => Promise.resolve(false),
  sessionGet: () => Promise.resolve(null),
  sessionSet: done,
  sessionClear: done,

  outboxPut: done,
  outboxRemove: done,
  outboxAck: done,
  outboxAll: () => Promise.resolve([]),
  outboxClear: done,
  outboxClearLedger: done,
  onNativeRowsLogged: () => () => undefined,

  writeSnapshot: done,
  haptics: done,
  powerSave: () => Promise.resolve(false),
  requestNotificationPermission: () => Promise.resolve(false),
  scheduleNotifications: done,
  clearAll: done,

  // A browser has no app links to receive and no hardware back to intercept:
  // the address bar and the browser's own back button are the web's versions.
  onDeepLink: () => () => undefined,
  onBackButton: () => () => undefined,
  exitApp: done,
};

/** The name `@/native/impl` exports in every mode, so the alias can swap whole files. */
export const bridge: NativeBridge = webBridge;
